import h from 'hyperscript';

import { createResourceLabel } from 'ui/resource-label/resource-label';
import i18n from 'ui/attribute-list/attribute-list.properties';
import { createAnchorButton } from 'ui/button/button';

import { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import type { OutdatedEnrichmentsContext } from 'cadenza/workbook/workbook-panel/databrowser/outdated-enrichments-context';

import './enrichment-summary.css';

const COMPONENT_NAME = 'd-enrichment-summary';

interface EnrichmentSummaryEnrichment {
  repositoryName: RepositoryName;
  enrichedObjecttypeId: ObjecttypeIdString;
  enrichmentObjecttypeId: ObjecttypeIdString;
  printName: string;
  grouped: boolean;
  enrichmentType: EnrichmentType;
}

export class EnrichmentSummary extends HTMLElement {
  constructor(enrichment: EnrichmentSummaryEnrichment, outdatedEnrichmentsContext?: OutdatedEnrichmentsContext) {
    super();
    this.classList.add(COMPONENT_NAME);
    this.append(this.#createContent(enrichment, outdatedEnrichmentsContext));
  }

  #createContent(enrichment: EnrichmentSummaryEnrichment, outdatedEnrichmentContext?: OutdatedEnrichmentsContext) {
    const id = enrichment.enrichmentObjecttypeId;
    const summaryChildren = [
      h(
        '.h1',
        createResourceLabel('enrichment', enrichment.printName, {
          styleClass: `${COMPONENT_NAME}--enrichment-resource-label`,
        }),
      ),
      h('span.text-muted', EnrichmentType.i18n(enrichment.enrichmentType)),
    ];
    if (outdatedEnrichmentContext?.isOutdated(id)) {
      summaryChildren.push(createEnrichmentOutdatedAlert(id, outdatedEnrichmentContext));
    }
    return h('summary.stack-v', summaryChildren);
  }
}

customElements.define(COMPONENT_NAME, EnrichmentSummary);

export function createEnrichmentOutdatedAlert(
  enrichmentObjecttypeId: ObjecttypeIdString,
  outdatedEnrichmentsContext: OutdatedEnrichmentsContext,
) {
  const isEnrichmentDirectlyOutdated = outdatedEnrichmentsContext.isDirectlyOutdated(enrichmentObjecttypeId);
  const alertText =
    isEnrichmentDirectlyOutdated ?
      i18n('enrichmentGroup.warning.directlyOutdated.text')
    : i18n('enrichmentGroup.warning.indirectlyOutdated.text');
  const alert = h(
    'd-alert.d-alert-with-button-bottom',
    { attrs: { size: 's', type: 'warning' } },
    h('.d-alert--text', alertText),
  );
  if (isEnrichmentDirectlyOutdated) {
    alert.append(
      createAnchorButton(i18n('enrichmentGroup.warning.directlyOutdated.link'), {
        onclick: () => outdatedEnrichmentsContext.rerun(enrichmentObjecttypeId),
      }),
    );
  }
  return alert;
}
